/**
 * React-query hooks for the public.translations table — V2 admin.
 *
 * Backed by the supabase client (anon role on the front-end). RLS on
 * translations is "anyone can SELECT, admin-only INSERT/UPDATE/DELETE",
 * so the list query works for everyone but mutations only succeed for
 * authenticated admins (PostgREST surfaces a permission error otherwise,
 * which the standard react-query error path handles).
 *
 * Why a dedicated hook file (instead of folding into LanguageContext):
 * the admin screen needs the raw row shape (key + en + de + namespace +
 * updated_at) for editing, while LanguageContext only needs a flat
 * dict for the active language. Different consumers, different shapes.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type TranslationRow = {
  key: string;
  en: string | null;
  de: string | null;
  namespace: string;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
};

export type TranslationInput = {
  key: string;
  en: string | null;
  de: string | null;
  description?: string | null;
};

const QK = {
  list: () => ["v2", "translations", "list"] as const,
};

/**
 * List every translation row, sorted by key. ~300 rows total — small
 * enough that we don't paginate server-side; the admin screen filters
 * client-side for instant search.
 */
export function useTranslations() {
  return useQuery({
    queryKey: QK.list(),
    queryFn: async (): Promise<TranslationRow[]> => {
      const { data, error } = await supabase
        .from("translations")
        .select("key, en, de, namespace, description, updated_at, updated_by")
        .order("key", { ascending: true });
      if (error) throw error;
      return (data ?? []) as TranslationRow[];
    },
    staleTime: 30_000,
  });
}

/**
 * Upsert a single translation row. Used by both inline-edit (existing key)
 * and add-key dialog (new key) — the upsert semantics handle both.
 *
 * After success: invalidate the V2 admin list AND the public-site
 * `translations` query (the one LanguageContext uses), so an admin's
 * edit propagates to the live site without a manual refresh.
 */
export function useUpsertTranslation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: TranslationInput): Promise<TranslationRow> => {
      const { data, error } = await supabase
        .from("translations")
        .upsert(
          {
            key: input.key.trim(),
            en: input.en?.trim() || null,
            de: input.de?.trim() || null,
            description: input.description?.trim() || null,
          },
          { onConflict: "key" }
        )
        .select("key, en, de, namespace, description, updated_at, updated_by")
        .single();
      if (error) throw error;
      return data as TranslationRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.list() });
      // The public-site LanguageContext keys translations queries by language.
      qc.invalidateQueries({ queryKey: ["translations"] });
    },
  });
}

/**
 * Delete a translation row by key. Rare — most admin edits set values
 * to empty rather than removing the key, since callsites in code still
 * reference it. Exposed for genuine cleanup of dead keys.
 */
export function useDeleteTranslation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string): Promise<void> => {
      const { error } = await supabase
        .from("translations")
        .delete()
        .eq("key", key);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.list() });
      qc.invalidateQueries({ queryKey: ["translations"] });
    },
  });
}
